// src/components/admin/settings/AdminSettings.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Settings, DollarSign, Globe, Bell, Shield, Target, FileText, Lock } from 'lucide-react';
import { toast } from 'react-hot-toast';

import styles from '../../../styles/Settings.module.css';
import useTranslation from '../../../hooks/useTranslation';

// Redux imports
import {
  getAllSettings,
  resetToDefaults,
  clearSettingsErrors,
  clearSettingsSuccess,
  selectSettingsLoading,
  selectSettingsUpdating,
  selectSettingsError,
  selectUpdateSettingsError,
  selectSettingsUpdateSuccess,
} from '../../../store/slices/platformSettingsSlice';

// Import sub-components
import DonationSettings from './DonationSettings';
import DonationGoals from './DonationGoals';
import GeneralSettings from './GeneralSettings';
import NotificationSettings from './NotificationSettings';
import FeatureSettings from './FeatureSettings';
import SocialLinks from './SocialLinks';
import StripeSettings from './StripeSettings';
import TermsSettings from './TermsSettings';
import PrivacySettings from './PrivacySettings';

const AdSettings = () => {
  const dispatch = useDispatch();
  
  // Translation hooks
  const { translatedText: platformSettings } = useTranslation('Platform Settings');
  const { translatedText: configureManage } = useTranslation('Configure and manage your platform settings');
  const { translatedText: reset } = useTranslation('Reset');
  const { translatedText: saveChanges } = useTranslation('Save Changes');
  const { translatedText: savingText } = useTranslation('Saving...');
  const { translatedText: donationSettings } = useTranslation('Donation Settings');
  const { translatedText: donationGoals } = useTranslation('Donation Goals');
  const { translatedText: generalSettings } = useTranslation('General Settings');
  const { translatedText: notificationSettings } = useTranslation('Notification Settings');
  const { translatedText: platformFeatures } = useTranslation('Platform Features');
  const { translatedText: socialLinks } = useTranslation('Social Links');
  const { translatedText: textStripeConfig } = useTranslation('Stripe Configuration');
  const { translatedText: termsOfService } = useTranslation('Terms of Service');
  const { translatedText: privacyPolicy } = useTranslation('Privacy Policy');
  const { translatedText: loadingSettings } = useTranslation('Loading settings...');
  const { translatedText: allSettingsSaved } = useTranslation('All settings saved successfully!');
  const { translatedText: settingsReset } = useTranslation('Settings reset to default');
  const { translatedText: resetConfirm } = useTranslation('Are you sure you want to reset all settings to default?');

  // Redux state selectors
  const loading = useSelector(selectSettingsLoading);
  const isSaving = useSelector(selectSettingsUpdating);
  const settingsError = useSelector(selectSettingsError);
  const updateError = useSelector(selectUpdateSettingsError);
  const updateSuccess = useSelector(selectSettingsUpdateSuccess);

  const [activeTab, setActiveTab] = useState('donations');

  // Load settings on mount
  useEffect(() => {
    dispatch(getAllSettings());
  }, [dispatch]);

  // Handle success/error toasts
  useEffect(() => {
    if (updateSuccess) {
      toast.success(allSettingsSaved || 'All settings saved successfully!');
      dispatch(clearSettingsSuccess());
    }
  }, [updateSuccess, allSettingsSaved, dispatch]);

  useEffect(() => {
    if (updateError) {
      toast.error(updateError);
      dispatch(clearSettingsErrors());
    }
  }, [updateError, dispatch]);

  useEffect(() => {
    if (settingsError) {
      toast.error(settingsError);
      dispatch(clearSettingsErrors());
    }
  }, [settingsError, dispatch]);

  const resetSettings = () => {
    if (window.confirm(resetConfirm || 'Are you sure you want to reset all settings to default?')) {
      dispatch(resetToDefaults())
        .unwrap()
        .then(() => {
          dispatch(getAllSettings());
          toast.success(settingsReset || 'Settings reset to default');
        })
        .catch(error => {
          toast.error(error || 'Failed to reset settings');
        });
    }
  };

  const tabs = [
    { id: 'donations', label: donationSettings || 'Donations', icon: DollarSign },
    { id: 'goals', label: donationGoals || 'Donation Goals', icon: Target },
    { id: 'general', label: generalSettings || 'General', icon: Globe },
    { id: 'notifications', label: notificationSettings || 'Notifications', icon: Bell },
    { id: 'features', label: platformFeatures || 'Features', icon: Settings },
    { id: 'social', label: socialLinks || 'Social Links', icon: Globe },
    { id: 'stripe', label: textStripeConfig || 'Stripe', icon: Shield },
    { id: 'terms', label: termsOfService || 'Terms', icon: FileText },
    { id: 'privacy', label: privacyPolicy || 'Privacy', icon: Lock }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'donations': return <DonationSettings />;
      case 'goals': return <DonationGoals />;
      case 'general': return <GeneralSettings />;
      case 'notifications': return <NotificationSettings />;
      case 'features': return <FeatureSettings />;
      case 'social': return <SocialLinks />;
      case 'stripe': return <StripeSettings />;
      case 'terms': return <TermsSettings />;
      case 'privacy': return <PrivacySettings />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>{loadingSettings || 'Loading settings...'}</p>
      </div>
    );
  }

  return (
    <div className={styles.settingsContainer}>
      <div className={styles.sectionHeader}>
        <div className={styles.headerLeft}>
          <h2>{platformSettings || 'Platform Settings'}</h2>
          <p className={styles.subtitle}>{configureManage || 'Configure and manage your platform settings'}</p>
        </div>
        <div className={styles.headerActions}>
          <button 
            className={styles.resetBtn}
            onClick={resetSettings}
            disabled={isSaving}
          >
            <span className={isSaving ? styles.spinning : ''}>↺</span>
            {reset || 'Reset'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabsContainer}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tabBtn} ${activeTab === tab.id ? styles.active : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className={styles.settingsContent}>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default AdSettings;